import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import itemSetsService from '../../services/itemSetsService';
import { inventoryService } from '../../services/api';

const ItemSetModal = ({ open, onClose, onSelect, masterItems, printMode = false, assemblySingleLineOption = false }) => {
  // Get itemSets from Redux store (read-only)
  const reduxItemSets = useSelector((state) => state.masterData?.itemSets || []);
  
  const [currentTab, setCurrentTab] = useState(0); // 0 = Select, 1 = Create/Edit
  const [itemSets, setItemSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSet, setEditingSet] = useState(null);
  /** When true, selecting a set adds one sale line; inventory parts are issued on delivery/invoice (server). */
  const [assemblySingleLine, setAssemblySingleLine] = useState(false);
  
  // Build a map of all items from itemSets for lookup
  // This helps find item names even when items aren't in masterItems
  const itemSetItemsMap = useMemo(() => {
    const map = new Map();
    if (reduxItemSets && Array.isArray(reduxItemSets)) {
      reduxItemSets.forEach(set => {
        if (set.items && Array.isArray(set.items)) {
          set.items.forEach(item => {
            if (item.itemId != null) {
              const key = String(item.itemId);
              if (!map.has(key)) {
                // Store the itemId with its set context for reference
                map.set(key, {
                  itemId: item.itemId,
                  quantity: item.quantity,
                  setName: set.name
                });
              }
            }
          });
        }
      });
    }
    return map;
  }, [reduxItemSets]);
  
  // State to store fetched items that aren't in masterItems
  const [fetchedItems, setFetchedItems] = useState({});
  
  // Function to fetch an item by ID if not found in masterItems
  const fetchItemById = async (itemId) => {
    if (!itemId) return null;
    
    // Check if already fetched
    if (fetchedItems[itemId] || fetchedItems[String(itemId)] || fetchedItems[Number(itemId)]) {
      return fetchedItems[itemId] || fetchedItems[String(itemId)] || fetchedItems[Number(itemId)];
    }
    
    try {
      const item = await inventoryService.getInventoryItem(itemId);
      if (item) {
        setFetchedItems(prev => ({
          ...prev,
          [itemId]: item,
          [String(itemId)]: item,
          [Number(itemId)]: item
        }));
        return item;
      }
    } catch (error) {
      console.warn(`Could not fetch item ${itemId}:`, error);
    }
    return null;
  };
  
  // Fetch missing items when editing a set
  useEffect(() => {
    if (editingSet && editingSet.items && Array.isArray(editingSet.items)) {
      editingSet.items.forEach(item => {
        if (item.itemId != null) {
          // Check if item is not in masterItems
          const found = masterItems?.find(mi => {
            const id = mi.id || mi._id;
            return id != null && (
              id === item.itemId ||
              Number(id) === Number(item.itemId) ||
              String(id) === String(item.itemId)
            );
          });
          
          if (!found) {
            // Item not in masterItems, fetch it
            fetchItemById(item.itemId);
          }
        }
      });
    }
  }, [editingSet, masterItems]);
  
  // Store search state for each item (by index): { searchQuery, searchResults, loading }
  const [itemSearches, setItemSearches] = useState({});
  const abortControllersRef = useRef({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    items: [{ itemId: '', quantity: '' }]
  });

  // Get or initialize search state for an item index
  const getItemSearchState = (index) => {
    if (!itemSearches[index]) {
      return {
        searchQuery: '',
        searchResults: [],
        loading: false
      };
    }
    return itemSearches[index];
  };

  // Set search query for a specific item
  const setItemSearchQuery = (index, query) => {
    setItemSearches(prev => ({
      ...prev,
      [index]: {
        ...getItemSearchState(index),
        searchQuery: query
      }
    }));
  };

  // Perform search for a specific item
  const handleItemSearch = async (index, query) => {
    const trimmedQuery = query.trim();

    // If search is empty, clear results
    if (!trimmedQuery) {
      setItemSearches(prev => ({
        ...prev,
        [index]: {
          searchQuery: '',
          searchResults: [],
          loading: false
        }
      }));
      return;
    }

    // Cancel previous request for this index
    if (abortControllersRef.current[index]) {
      abortControllersRef.current[index].abort();
    }

    // Set loading state
    setItemSearches(prev => ({
      ...prev,
      [index]: {
        ...getItemSearchState(index),
        loading: true
      }
    }));

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllersRef.current[index] = abortController;

    try {
      const response = await inventoryService.getInventoryItems({ 
        search: trimmedQuery, 
        limit: 5 
      });
      const items = response?.items || [];
      
      setItemSearches(prev => ({
        ...prev,
        [index]: {
          searchQuery: trimmedQuery,
          searchResults: items,
          loading: false
        }
      }));
    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Search error:', error);
      setItemSearches(prev => ({
        ...prev,
        [index]: {
          ...getItemSearchState(index),
          searchResults: [],
          loading: false
        }
      }));
    }
  };

  // Handle Enter key press for item search
  const handleItemKeyDown = (index, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const searchState = getItemSearchState(index);
      handleItemSearch(index, searchState.searchQuery);
    }
  };

  // Get combined options for an item (search results, fetched items, or masterItems)
  const getItemCombinedOptions = (index) => {
    const searchState = getItemSearchState(index);
    if (searchState.searchResults && searchState.searchResults.length > 0) {
      return searchState.searchResults;
    }
    
    // Combine masterItems with fetched items
    const fetchedItemsArray = Object.values(fetchedItems);
    if (fetchedItemsArray.length > 0 && masterItems) {
      // Merge fetched items with masterItems, avoiding duplicates
      const masterItemIds = new Set(
        masterItems.map(mi => String(mi.id || mi._id))
      );
      const uniqueFetchedItems = fetchedItemsArray.filter(
        item => !masterItemIds.has(String(item.id || item._id))
      );
      return [...masterItems, ...uniqueFetchedItems];
    }
    
    return masterItems || [];
  };

  // Fetch item sets when modal opens
  useEffect(() => {
    if (open) {
      fetchItemSets();
      // Reset form when opening
      setFormData({
        name: '',
        description: '',
        items: [{ itemId: '', quantity: '' }]
      });
      setEditingSet(null);
      setCurrentTab(0);
      setError('');
      setSuccess('');
      setItemSearches({});
      setFetchedItems({});
      setAssemblySingleLine(false);
      // Clean up abort controllers
      Object.values(abortControllersRef.current).forEach(controller => {
        if (controller) controller.abort();
      });
      abortControllersRef.current = {};
    }
  }, [open]);

  // Sync with Redux itemSets
  useEffect(() => {
    if (reduxItemSets && reduxItemSets.length > 0) {
      setItemSets(reduxItemSets);
    }
  }, [reduxItemSets]);

  const fetchItemSets = async () => {
    setLoading(true);
    setError('');
    try {
      const sets = await itemSetsService.getItemSets();
      const setsArray = Array.isArray(sets) ? sets : [];
      setItemSets(setsArray);
    } catch (err) {
      console.error('Error fetching item sets:', err);
      const errorMessage = err.response?.data?.error || err.error || err.message || 'Failed to fetch item sets';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    // Allow switching to any tab (including CREATE NEW SET)
    setCurrentTab(newValue);
    // Reset form when switching tabs
    setFormData({
      name: '',
      description: '',
      items: [{ itemId: '', quantity: '' }]
    });
    setEditingSet(null);
    setError('');
    setSuccess('');
    setItemSearches({});
    // Clean up abort controllers
    Object.values(abortControllersRef.current).forEach(controller => {
      if (controller) controller.abort();
    });
    abortControllersRef.current = {};
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { itemId: '', quantity: '' }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: newItems
      }));
      
      // Clean up search state and abort controller for removed item
      if (abortControllersRef.current[index]) {
        abortControllersRef.current[index].abort();
        delete abortControllersRef.current[index];
      }
      
      // Remove search state for this index
      setItemSearches(prev => {
        const newSearches = { ...prev };
        delete newSearches[index];
        return newSearches;
      });
    }
  };

  const handleEdit = (itemSet) => {
    setEditingSet(itemSet);
    // Ensure we preserve ALL items exactly as they come from the backend
    const items = itemSet.items && Array.isArray(itemSet.items) && itemSet.items.length > 0
      ? itemSet.items.map(item => {
          // Preserve itemId as-is (could be number or string)
          const itemId = item.itemId != null ? item.itemId : '';
          // Preserve quantity
          const quantity = item.quantity != null ? String(item.quantity) : '';
          return { itemId, quantity };
        })
      : [{ itemId: '', quantity: '' }];
    
    setFormData({
      name: itemSet.name,
      description: itemSet.description || '',
      items: items
    });
    setCurrentTab(1); // Switch to create/edit tab
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item set?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await itemSetsService.deleteItemSet(id);
      setSuccess('Item set deleted successfully');
      await fetchItemSets();
      // If we were editing this set, reset form
      if (editingSet && editingSet.id === id) {
        setEditingSet(null);
        setFormData({
          name: '',
          description: '',
          items: [{ itemId: '', quantity: '' }]
        });
        setCurrentTab(0);
      }
    } catch (err) {
      console.error('Error deleting item set:', err);
      const errorMessage = err.response?.data?.error || err.error || err.message || 'Failed to delete item set';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate form
    if (!formData.name || !formData.name.trim()) {
      setError('Set name is required');
      return;
    }

    if (!formData.items || formData.items.length === 0) {
      setError('At least one item is required');
      return;
    }

    // Validate all items have itemId and quantity
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!item.itemId || !item.quantity || item.quantity <= 0) {
        setError(`Item ${i + 1} must have a valid item and quantity > 0`);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        items: formData.items.map(item => ({
          itemId: Number(item.itemId), // Ensure itemId is a number
          quantity: Number(item.quantity)
        }))
      };

      if (editingSet) {
        await itemSetsService.updateItemSet(editingSet.id, payload);
        setSuccess('Item set updated successfully');
      } else {
        await itemSetsService.createItemSet(payload);
        setSuccess('Item set created successfully');
      }
      
      // Trigger a refetch of master data to update Redux store
      // This will be handled by the parent component or app-level masterData fetch

      // Refresh item sets
      await fetchItemSets();

      // Reset form
      setFormData({
        name: '',
        description: '',
        items: [{ itemId: '', quantity: '' }]
      });
      setEditingSet(null);
      
      // Switch back to select tab after a short delay
      setTimeout(() => {
        setCurrentTab(0);
        setSuccess('');
      }, 1000);
    } catch (err) {
      console.error('Error saving item set:', err);
      const errorMessage = err.response?.data?.error || err.error || err.message || 'Failed to save item set';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSet = async (itemSet) => {
    if (onSelect && itemSet.items && Array.isArray(itemSet.items)) {
      console.log('ItemSetModal: handleSelectSet - itemSet.items:', itemSet.items);
      console.log('ItemSetModal: itemSet.items.length:', itemSet.items.length);
      
      // First, fetch all missing items (same approach as edit mode)
      // This ensures all items are available for mapping
      const fetchResults = await Promise.all(
        itemSet.items
          .filter(setItem => setItem.itemId != null)
          .map(async (setItem) => {
            // Check if item is in masterItems
            const found = masterItems?.find(mi => {
              const id = mi.id || mi._id;
              return id != null && (
                id === setItem.itemId ||
                Number(id) === Number(setItem.itemId) ||
                String(id) === String(setItem.itemId)
              );
            });
            
            // If found in masterItems, return it
            if (found) {
              return { itemId: setItem.itemId, item: found };
            }
            
            // Check if already in fetchedItems
            const existingFetched = fetchedItems[setItem.itemId] || 
                                   fetchedItems[String(setItem.itemId)] || 
                                   fetchedItems[Number(setItem.itemId)];
            if (existingFetched) {
              return { itemId: setItem.itemId, item: existingFetched };
            }
            
            // Not found, fetch it
            const fetchedItem = await fetchItemById(setItem.itemId);
            return { itemId: setItem.itemId, item: fetchedItem };
          })
      );
      
      // Update fetchedItems state with all newly fetched items
      const newlyFetchedItems = {};
      fetchResults.forEach(result => {
        if (result.item && !masterItems?.find(mi => {
          const id = mi.id || mi._id;
          return id != null && (
            id === result.itemId ||
            Number(id) === Number(result.itemId) ||
            String(id) === String(result.itemId)
          );
        })) {
          newlyFetchedItems[result.itemId] = result.item;
          newlyFetchedItems[String(result.itemId)] = result.item;
          newlyFetchedItems[Number(result.itemId)] = result.item;
        }
      });
      
      // Update state if we have new items
      if (Object.keys(newlyFetchedItems).length > 0) {
        setFetchedItems(prev => ({ ...prev, ...newlyFetchedItems }));
      }
      
      // Build a map of itemIds to items from fetchResults
      const itemsMap = new Map();
      
      // Add all masterItems to the map
      if (masterItems) {
        masterItems.forEach(mi => {
          const id = mi.id || mi._id;
          if (id != null) {
            itemsMap.set(id, mi);
            itemsMap.set(Number(id), mi);
            itemsMap.set(String(id), mi);
          }
        });
      }
      
      // Add fetched items from fetchResults to the map
      fetchResults.forEach(result => {
        if (result.item) {
          const id = result.item.id || result.item._id || result.itemId;
          if (id != null) {
            itemsMap.set(id, result.item);
            itemsMap.set(Number(id), result.item);
            itemsMap.set(String(id), result.item);
          }
        }
      });
      
      // Map ALL items from set format to form format
      // The parent form expects item.item to be the item ID (number)
      // Sales form structure: { item: number, quantity: string, variantId: null, variantName: null }
      console.log('ItemSetModal: Before filtering, itemSet.items.length:', itemSet.items.length);
      
      const mappedItems = itemSet.items
        .filter(setItem => {
          const hasValidId = setItem.itemId != null;
          if (!hasValidId) {
            console.warn('ItemSetModal: Filtering out item with invalid itemId:', setItem);
          }
          return hasValidId;
        }) // Only include items with valid itemId
        .map(setItem => {
          // Find the item object from our map
          const itemObj = itemsMap.get(setItem.itemId) ||
                         itemsMap.get(Number(setItem.itemId)) ||
                         itemsMap.get(String(setItem.itemId));
          
          // Ensure itemId is consistently a number (parent form matches by ID)
          const itemId = Number(setItem.itemId);
          
          // Return item in the format expected by Sales form
          // Also include the full item object so parent can use it for display
          return {
            item: itemId, // Item ID as number
            quantity: setItem.quantity != null ? String(setItem.quantity) : '1',
            variantId: null, // No variant selected from set
            variantName: null, // No variant selected from set
            _itemObject: itemObj || null // Full item object for display purposes
          };
        });
      
      // Extract all fetched item objects to pass to parent
      const fetchedItemObjects = Array.from(new Set(
        mappedItems
          .map(mi => mi._itemObject)
          .filter(item => item != null)
      ));
      
      console.log('ItemSetModal: After filtering and mapping, mappedItems.length:', mappedItems.length);
      console.log('ItemSetModal: mappedItems:', mappedItems);
      console.log('ItemSetModal: fetchedItemObjects:', fetchedItemObjects);

      if (assemblySingleLine && assemblySingleLineOption && mappedItems.length > 0) {
        const bomSnapshot = itemSet.items
          .filter((setItem) => setItem.itemId != null)
          .map((setItem) => ({
            itemId: Number(setItem.itemId),
            quantity: Number(setItem.quantity) || 0,
            variantId: null,
            variantName: null
          }))
          .filter((r) => r.itemId != null && !Number.isNaN(r.itemId) && r.quantity > 0);
        if (bomSnapshot.length === 0) {
          setError('No valid components in this set for inventory consumption.');
          return;
        }
        let suggestedRate = 0;
        for (const mi of mappedItems) {
          const obj =
            itemsMap.get(mi.item) ||
            itemsMap.get(Number(mi.item)) ||
            itemsMap.get(String(mi.item));
          const q = Number(mi.quantity) || 0;
          const p = Number(obj?.retailPrice) || 0;
          suggestedRate += p * q;
        }
        const rounded = Math.round(suggestedRate * 100) / 100;
        const assemblyLine = {
          item: '',
          quantity: '1',
          rate: String(rounded),
          unitPrice: String(rounded),
          tax: '',
          total: '',
          variantId: null,
          variantName: null,
          categoryId: '',
          subcategoryId: '',
          deductInventoryViaBom: true,
          bomSnapshot,
          assemblySourceItemSetId: itemSet.id ?? itemSet._id ?? null,
          assemblyName: itemSet.name || 'Custom product',
          assemblyDescription: (itemSet.description && String(itemSet.description).trim()) || '',
          productName: itemSet.name || 'Custom product',
          productDescription: (itemSet.description && String(itemSet.description).trim()) || ''
        };
        onSelect([assemblyLine], []);
        onClose();
        return;
      }

      // Call onSelect with all items and fetched item objects
      if (mappedItems.length > 0) {
        console.log('ItemSetModal: Passing', mappedItems.length, 'items to parent form');
        onSelect(mappedItems, fetchedItemObjects);
        onClose();
      } else {
        setError('No valid items found in the selected set. Please ensure all items exist in inventory.');
      }
    }
  };

  const handlePrintChecklist = async (itemSet) => {
    if (!itemSet || !itemSet.items || !Array.isArray(itemSet.items) || itemSet.items.length === 0) {
      setError('This item set has no items to print');
      return;
    }

    // Fetch all items to get their names
    const itemsWithNames = await Promise.all(
      itemSet.items
        .filter(setItem => setItem.itemId != null)
        .map(async (setItem) => {
          // Check if item is in masterItems
          const found = masterItems?.find(mi => {
            const id = mi.id || mi._id;
            return id != null && (
              id === setItem.itemId ||
              Number(id) === Number(setItem.itemId) ||
              String(id) === String(setItem.itemId)
            );
          });
          
          if (found) {
            return { itemName: found.name, quantity: Number(setItem.quantity) || 0 };
          }
          
          // Check if already in fetchedItems
          const existingFetched = fetchedItems[setItem.itemId] || 
                                 fetchedItems[String(setItem.itemId)] || 
                                 fetchedItems[Number(setItem.itemId)];
          if (existingFetched) {
            return { itemName: existingFetched.name, quantity: Number(setItem.quantity) || 0 };
          }
          
          // Not found, fetch it
          const fetchedItem = await fetchItemById(setItem.itemId);
          return { itemName: fetchedItem?.name || `Item ID: ${setItem.itemId}`, quantity: Number(setItem.quantity) || 0 };
        })
    );

    // Generate print HTML
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    // Generate items list with checkboxes
    let itemsHTML = "";
    if (itemsWithNames.length > 0) {
      itemsHTML = itemsWithNames
        .map((item) => {
          const quantity = item.quantity || 0;
          const itemName = item.itemName || "N/A";

          // Generate unit columns (each with label and checkbox)
          const unitColumns = Array.from({ length: quantity }, (_, index) => {
            return `
              <div class="unit-column">
                <div class="unit-header">Unit ${index + 1}</div>
                <div class="checkbox-box"></div>
              </div>
            `;
          }).join("");

          return `
          <div class="item-group">
            <div class="item-row">
              <div class="item-name">${itemName}</div>
              <div class="units-container">
                ${unitColumns}
              </div>
            </div>
          </div>
        `;
        })
        .join("");
    } else {
      itemsHTML = '<div class="no-items">No items in this set.</div>';
    }

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Items List - ${itemSet.name}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              padding: 20px;
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #000;
              background: #fff;
            }
            .container {
              max-width: 800px;
              margin: 0 auto;
              background: #fff;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #333;
            }
            .header h4 {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .header .job-title {
              font-size: 18px;
              color: #555;
              margin-bottom: 10px;
            }
            .header .job-info {
              font-size: 12px;
              color: #777;
              display: flex;
              justify-content: space-around;
              margin-top: 15px;
            }
            .header .job-info div {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
            .header .job-info strong {
              font-size: 11px;
              text-transform: uppercase;
              color: #333;
            }
            .items-section {
              margin-top: 30px;
            }
            .items-section h4 {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 20px;
              text-align: center;
              text-transform: uppercase;
              border-bottom: 1px solid #333;
              padding-bottom: 10px;
            }
            .item-group {
              border: 1px solid #ddd;
              padding: 10px;
              background-color: #fafafa;
            }
            .item-row {
              display: flex;
              align-items: flex-start;
              gap: 10px;
            }
            .item-name {
              font-size: 16px;
              font-weight: bold;
              color: #333;
              min-width: 200px;
              padding-top: 5px;
            }
            .units-container {
              flex: 1;
              display: flex;
              gap: 15px;
              flex-wrap: wrap;
            }
            .unit-column {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
              min-width:40px;
            }
            .unit-header {
              font-size: 12px;
              color: #666;
              text-align: center;
            }
            .checkbox-box {
              width: 15px;
              height: 15px;
              border: 1px solid #000;
              display: block;
              flex-shrink: 0;
              background-color: #fff;
            }
            .no-items {
              text-align: center;
              padding: 40px;
              color: #999;
              font-style: italic;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #333;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body {
                padding: 10px;
              }
              .container {
                border: none;
                padding: 20px;
              }
              @page {
                size: letter;
                margin: 0.5in;
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h4>ITEMS REQUIRED FOR JOB</h4>
              <div class="job-title">${itemSet.name || "N/A"}</div>
              <div class="job-info">
                <div>
                  <strong>Date:</strong>
                  <span>${formattedDate}</span>
                </div>
                <div>
                  <strong>Due Date:</strong>
                  <span>${formattedDate}</span>
                </div>
                <div>
                  <strong>Location:</strong>
                  <span>${itemSet.description || "N/A"}</span>
                </div>
              </div>
            </div>
            
            <div class="items-section">
              <h4>Items Checklist</h4>
              ${itemsHTML}
            </div>
            
            <div class="footer">
              <p>Print Date: ${formattedDate}</p>
              <p>Use this checklist to collect items from inventory. Check each item as you collect it.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    // Create a hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow || iframe.contentDocument;
    if (iframeDoc.document) {
      iframeDoc.document.open();
      iframeDoc.document.write(printHTML);
      iframeDoc.document.close();
    } else {
      iframeDoc.open();
      iframeDoc.write(printHTML);
      iframeDoc.close();
    }

    let hasPrinted = false; // Flag to prevent double printing

    // Wait for content to load, then print
    iframe.onload = function () {
      if (hasPrinted) return; // Already printed, skip
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        hasPrinted = true;

        // Remove iframe after a delay to allow print dialog to open
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      } catch (error) {
        console.error("Error printing checklist:", error);
        setError("Unable to print checklist. Please try again.");
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }
    };

    // Fallback: if onload doesn't fire, try printing after a short delay
    setTimeout(() => {
      if (hasPrinted) return; // Already printed, skip
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          hasPrinted = true;
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }
      } catch (error) {
        console.error("Error printing checklist:", error);
        setError("Unable to print checklist. Please try again.");
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }
    }, 500);
  };

  const getItemName = (itemId) => {
    if (!masterItems || !itemId) return 'Unknown Item';
    const item = masterItems.find(mi => (mi.id || mi._id) === itemId);
    return item ? item.name : 'Unknown Item';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
        <Typography variant="h6">Item Sets</Typography>
        <IconButton onClick={onClose} size="small" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="SELECT EXISTING SET" />
          <Tab label={editingSet ? "Edit Set" : "CREATE NEW SET"} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {currentTab === 0 && (
          <Box>
            {assemblySingleLineOption && !printMode && (
              <FormControlLabel
                sx={{ mb: 2, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={assemblySingleLine}
                    onChange={(e) => setAssemblySingleLine(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" display="block">
                      Add as one custom product
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Parts are deducted from inventory when the sale is set to Delivery or Invoice.
                    </Typography>
                  </Box>
                }
              />
            )}
            {loading && !itemSets.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : itemSets.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                No item sets found. Create a new set using the "Create New Set" tab.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemSets.map((set) => (
                      <TableRow key={set.id || set._id} hover>
                        <TableCell>{set.name}</TableCell>
                        <TableCell>
                          {set.description || '-'}
                        </TableCell>
                        <TableCell>
                          {set.items && set.items.length > 0
                            ? `${set.items.length} item(s)`
                            : 'No items'}
                        </TableCell>
                        <TableCell align="right">
                          {printMode ? (
                            <>
                              <Tooltip title="Print Checklist">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handlePrintChecklist(set)}
                                  disabled={loading}
                                >
                                  <PrintIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Set">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(set)}
                                  disabled={loading}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Set">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(set.id || set._id)}
                                  disabled={loading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Select Set">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSelectSet(set)}
                                  disabled={loading}
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Set">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(set)}
                                  disabled={loading}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Set">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(set.id || set._id)}
                                  disabled={loading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {currentTab === 1 && (
          <Box>
            <TextField
              label="Set Name *"
              fullWidth
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              sx={{ mb: 2, '& .MuiInputBase-input': { padding: '15px' } }}
              required
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Items *
            </Typography>

            {formData.items.map((item, index) => {
              const searchState = getItemSearchState(index);
              const combinedOptions = getItemCombinedOptions(index);
              
              // Normalize itemId for comparison (handle both string and number)
              // Convert to string for comparison to handle type mismatches
              const itemIdStr = item.itemId != null ? String(item.itemId) : '';
              const itemIdNum = item.itemId != null && !isNaN(Number(item.itemId)) ? Number(item.itemId) : null;
              
              // Check combinedOptions first (has latest data from backend)
              // Compare with both number and string to handle type mismatches
              let selectedItem = combinedOptions.find(i => {
                const id = i.id || i._id;
                if (id == null) return false;
                // Try multiple comparison methods
                return String(id) === itemIdStr || 
                       (itemIdNum != null && Number(id) === itemIdNum) ||
                       id === item.itemId;
              });
              
              // Fallback to masterItems if not found in search results
              if (!selectedItem && item.itemId != null && item.itemId !== '') {
                selectedItem = masterItems?.find(mi => {
                  const id = mi.id || mi._id;
                  if (id == null) return false;
                  // Try multiple comparison methods
                  return String(id) === itemIdStr || 
                         (itemIdNum != null && Number(id) === itemIdNum) ||
                         id === item.itemId;
                });
              }
              
              // If item not found but we have an itemId, try to find it in itemSetItemsMap
              // or create a placeholder item object
              if (!selectedItem && item.itemId != null && item.itemId !== '') {
                // Check if this itemId exists in any item set (might help with display)
                const itemSetItem = itemSetItemsMap.get(item.itemId) || 
                                   itemSetItemsMap.get(itemIdNum) ||
                                   itemSetItemsMap.get(itemIdStr);
                
                // Try to find item by searching in all masterItems (maybe it's there but comparison failed)
                if (!selectedItem && masterItems) {
                  // Try direct number comparison
                  selectedItem = masterItems.find(mi => {
                    const id = mi.id || mi._id;
                    return id != null && (
                      id === item.itemId ||
                      Number(id) === itemIdNum ||
                      String(id) === itemIdStr
                    );
                  });
                }
                
                // If still not found, create placeholder
                if (!selectedItem) {
                  selectedItem = {
                    id: itemIdNum != null ? itemIdNum : item.itemId,
                    _id: itemIdNum != null ? itemIdNum : item.itemId,
                    name: itemSetItem ? `Item from "${itemSetItem.setName}" (ID: ${item.itemId})` : `Item ID: ${item.itemId}`
                  };
                }
              }
              
              const selectedValue = selectedItem || null;
              
              // Use same pattern as Sales.jsx: if item selected, show name, otherwise show search query
              const currentInputValue = item.itemId 
                ? (selectedValue ? selectedValue.name : '') 
                : searchState.searchQuery; 
              
              return (
                <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    options={combinedOptions}
                    getOptionLabel={(option) => option?.name || ''}
                    value={selectedValue}
                    inputValue={currentInputValue}
                    onChange={(event, newValue) => {
                      handleItemChange(index, 'itemId', newValue ? (newValue.id || newValue._id) : '');
                      // Clear search query when item is selected
                      if (newValue) {
                        setItemSearchQuery(index, '');
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      // Only update search query when user is typing, not when selecting
                      if (reason === 'input') {
                        setItemSearchQuery(index, newInputValue);
                      } else if (reason === 'clear') {
                        setItemSearchQuery(index, '');
                        handleItemChange(index, 'itemId', '');
                      }
                    }}
                    onKeyDown={(event) => handleItemKeyDown(index, event)}
                    filterOptions={(options) => options}
                    loading={searchState.loading}
                    size="small"
                    sx={{ flex: 2 }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return (option.id || option._id) === (value.id || value._id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item *"
                        placeholder="Type and press Enter to search"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {searchState.loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    label="Quantity *"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    inputProps={{ min: 1 }}
                  />
                  {formData.items.length > 1 && (
                    <IconButton
                      onClick={() => removeItem(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              onClick={addItem}
              variant="outlined"
              size="small"
              sx={{ mb: 3 }}
            >
              Add Item
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {currentTab === 1 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name || !formData.name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : editingSet ? 'Update Set' : 'Create Set'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ItemSetModal;

